import { createGlobalStyle } from 'styled-components';
import { brandTheme } from './BrandTheme';

export const GlobalStyles = createGlobalStyle`

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
    line-height: 1.5;
  }

  body {
    font-family: ${brandTheme.typography.fontFamily.primary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: ${brandTheme.typography.lineHeight.relaxed};
    color: ${brandTheme.colors.secondary[800]};
    background-color: ${brandTheme.colors.neutral.white};
    font-weight: ${brandTheme.typography.fontWeight.normal};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${brandTheme.typography.fontFamily.heading};
    font-weight: ${brandTheme.typography.fontWeight.bold};
    line-height: ${brandTheme.typography.lineHeight.tight};
    margin-bottom: ${brandTheme.spacing[4]};
    color: ${brandTheme.colors.secondary[900]};
    letter-spacing: -0.025em;
  }

  h1 {
    font-size: clamp(2.25rem, 5vw, 4.5rem);
    font-weight: ${brandTheme.typography.fontWeight.extrabold};
    line-height: ${brandTheme.typography.lineHeight.none};
  }

  h2 {
    font-size: clamp(1.875rem, 4vw, 3rem);
    font-weight: ${brandTheme.typography.fontWeight.bold};
  }

  h3 {
    font-size: clamp(1.5rem, 3vw, 1.875rem);
    font-weight: ${brandTheme.typography.fontWeight.semibold};
  }

  h4 {
    font-size: clamp(1.25rem, 2.5vw, 1.5rem);
    font-weight: ${brandTheme.typography.fontWeight.semibold};
  }

  h5 {
    font-size: 1.125rem;
    font-weight: ${brandTheme.typography.fontWeight.medium};
  }

  h6 {
    font-size: 1rem;
    font-weight: ${brandTheme.typography.fontWeight.medium};
  }

  p {
    margin-bottom: ${brandTheme.spacing[4]};
    font-size: 1.125rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: ${brandTheme.typography.lineHeight.relaxed};
  }

  a {
    color: #FECE1B;
    text-decoration: none;
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    font-weight: ${brandTheme.typography.fontWeight.medium};
  }

  a:hover {
    color: #d97706;
    text-decoration: underline;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    font-size: inherit;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${brandTheme.spacing[4]};
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${brandTheme.spacing[2]};
    padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[8]};
    border-radius: ${brandTheme.borderRadius.full};
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    text-align: center;
    transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
    cursor: pointer;
    border: 2px solid transparent;
    font-size: 1.125rem;
    text-decoration: none;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }

    &:hover::before {
      left: 100%;
    }
  }

  .btn-primary {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%);
    color: ${brandTheme.colors.neutral.black};
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.3);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.4);
    }
  }

  .btn-secondary {
    background: transparent;
    color: #FECE1B;
    border: 2px solid #FECE1B;

    &:hover {
      background: #FECE1B;
      color: ${brandTheme.colors.neutral.black};
      transform: translateY(-2px);
    }
  }

  .btn-whatsapp {
    background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
    color: ${brandTheme.colors.neutral.white};
    box-shadow: 0 10px 30px rgba(37, 211, 102, 0.3);

    &:hover {
      background: linear-gradient(135deg, #20c55a 0%, #16a34a 100%);
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(37, 211, 102, 0.4);
    }
  }

  .section {
    padding: ${brandTheme.spacing[20]} 0;
  }

  .text-center {
    text-align: center;
  }

  .grid {
    display: grid;
    gap: ${brandTheme.spacing[8]};
  }

  .grid-2 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }

  .grid-3 {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }

  .grid-4 {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  /* Brand-specific utility classes */
  .brand-gradient {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .brand-shadow {
    box-shadow: ${brandTheme.boxShadow.xl};
  }

  .brand-border {
    border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  }

  .glass-effect {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  /* iPad Pro and Large Tablets (1024px - 1366px) */
  @media (max-width: 1366px) and (min-width: 1025px) {
    .container {
      padding: 0 2.5rem;
    }
    
    .section {
      padding: 5rem 0;
    }
    
    .btn {
      padding: 1rem 2rem;
      font-size: 1.125rem;
    }
    
    h1 {
      font-size: clamp(2.5rem, 4.5vw, 4rem);
    }
    
    h2 {
      font-size: clamp(2rem, 3.5vw, 2.75rem);
    }
    
    h3 {
      font-size: clamp(1.5rem, 2.5vw, 1.875rem);
    }
  }

  /* iPad Air and Medium Tablets (768px - 1024px) */
  @media (max-width: 1024px) and (min-width: 769px) {
    .container {
      padding: 0 2rem;
    }
    
    .section {
      padding: 4rem 0;
    }
    
    .btn {
      padding: 0.875rem 1.75rem;
      font-size: 1rem;
    }
    
    h1 {
      font-size: clamp(2rem, 4vw, 3.5rem);
    }
    
    h2 {
      font-size: clamp(1.75rem, 3.5vw, 2.5rem);
    }
    
    h3 {
      font-size: clamp(1.25rem, 2.5vw, 1.75rem);
    }
  }

  /* Landscape Mobile and Small Tablets (568px - 768px) */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    .container {
      padding: 0 1.5rem;
    }
    
    .section {
      padding: 2.5rem 0;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
    }
    
    h1 {
      font-size: clamp(1.75rem, 5vw, 2.25rem);
      line-height: 1.1;
    }
    
    h2 {
      font-size: clamp(1.5rem, 4vw, 1.875rem);
      line-height: 1.2;
    }
    
    h3 {
      font-size: clamp(1.25rem, 3vw, 1.5rem);
      line-height: 1.3;
    }
  }

  /* iPhone 12 Pro Max and Large Mobile (428px - 768px) */
  @media (max-width: 768px) and (min-width: 428px) {
    .container {
      padding: 0 1.25rem;
    }
    
    .section {
      padding: 3rem 0;
    }
    
    .btn {
      padding: 0.875rem 1.75rem;
      font-size: 1rem;
      min-height: 48px;
    }
    
    h1 {
      font-size: clamp(2rem, 7vw, 2.75rem);
      line-height: 1.1;
    }
    
    h2 {
      font-size: clamp(1.75rem, 5.5vw, 2.25rem);
      line-height: 1.2;
    }
    
    h3 {
      font-size: clamp(1.375rem, 4.5vw, 1.625rem);
      line-height: 1.3;
    }
    
    p {
      font-size: 1.125rem;
      line-height: 1.6;
    }
    
    .grid {
      gap: 1.75rem;
    }
    
    .grid-2,
    .grid-3,
    .grid-4 {
      grid-template-columns: 1fr;
    }
  }

  /* iPhone 12 and Standard Mobile (390px - 427px) */
  @media (max-width: 427px) and (min-width: 390px) {
    .container {
      padding: 0 1rem;
    }
    
    .section {
      padding: 2.5rem 0;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      font-size: 0.95rem;
      min-height: 44px;
    }
    
    h1 {
      font-size: clamp(1.75rem, 8vw, 2.5rem);
      line-height: 1.1;
    }
    
    h2 {
      font-size: clamp(1.5rem, 6vw, 2rem);
      line-height: 1.2;
    }
    
    h3 {
      font-size: clamp(1.25rem, 5vw, 1.5rem);
      line-height: 1.3;
    }
    
    p {
      font-size: 1rem;
      line-height: 1.6;
    }
    
    .grid {
      gap: 1.5rem;
    }
    
    .grid-2,
    .grid-3,
    .grid-4 {
      grid-template-columns: 1fr;
    }
  }

  /* iPhone SE and Small Mobile (320px - 389px) */
  @media (max-width: 389px) and (min-width: 320px) {
    .container {
      padding: 0 0.75rem;
    }
    
    .section {
      padding: 2rem 0;
    }
    
    .btn {
      padding: 0.625rem 1.25rem;
      font-size: 0.9rem;
      min-height: 44px;
    }
    
    h1 {
      font-size: clamp(1.5rem, 7vw, 2rem);
      line-height: 1.1;
    }
    
    h2 {
      font-size: clamp(1.25rem, 5.5vw, 1.75rem);
      line-height: 1.2;
    }
    
    h3 {
      font-size: clamp(1.125rem, 4.5vw, 1.375rem);
      line-height: 1.3;
    }
    
    p {
      font-size: 0.95rem;
      line-height: 1.5;
    }
    
    .grid {
      gap: 1.25rem;
    }
    
    .grid-2,
    .grid-3,
    .grid-4 {
      grid-template-columns: 1fr;
    }
  }

  /* Landscape Orientation Support */
  @media (orientation: landscape) and (max-height: 500px) {
    .section {
      padding: 1.5rem 0;
    }
    
    h1 {
      font-size: clamp(1.5rem, 4vw, 2rem);
      margin-bottom: 0.75rem;
    }
    
    h2 {
      font-size: clamp(1.25rem, 3.5vw, 1.75rem);
      margin-bottom: 0.75rem;
    }
    
    h3 {
      font-size: clamp(1.125rem, 3vw, 1.375rem);
      margin-bottom: 0.75rem;
    }
    
    p {
      margin-bottom: 0.75rem;
    }
    
    .btn {
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
    }
  }

  /* Ultra-wide Landscape (Tablets in landscape) */
  @media (orientation: landscape) and (min-width: 1024px) and (max-height: 768px) {
    .container {
      padding: 0 3rem;
    }
    
    .section {
      padding: 3rem 0;
    }
    
    h1 {
      font-size: clamp(2rem, 3vw, 2.5rem);
    }
    
    h2 {
      font-size: clamp(1.75rem, 2.5vw, 2rem);
    }
    
    h3 {
      font-size: clamp(1.5rem, 2vw, 1.75rem);
    }
  }

  /* Large Desktop Styles */
  @media (min-width: 1400px) {
    .container {
      max-width: 1400px;
      padding: 0 2rem;
    }
    
    .section {
      padding: 6rem 0;
    }
    
    h1 {
      font-size: clamp(3rem, 5vw, 4.5rem);
    }
    
    h2 {
      font-size: clamp(2.5rem, 4vw, 3.5rem);
    }
  }
`;
